import { Box, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useGetAccountAccess } from "@dealer-center/ddc-pages-configuration-fetch";
import type { MyInfoSubject } from "../../useMyInfoSubject";
import { SPACE_BLOCK } from "../../layoutTokens";
import { ReadOnlyNotice } from "../ReadOnlyNotice";
import { TabState } from "../TabState";
import { AccountNode } from "./AccountNode";

export interface AccountAccessTabProps {
  subject: MyInfoSubject;
}

/**
 * The subject's account hierarchy, nested to arbitrary depth, read-only (FR-028–FR-031).
 *
 * **Every returned node is listed, granted or not** (FR-031). That is T002's recorded outcome, not an
 * assumption: sampled across eight subjects on 2026-08-21 the node set was *identical* every time, so
 * the tree is the full hierarchy rather than a pre-filtered list of reachable accounts; and
 * `writeAccess: false` correlated exactly with holding zero `USER_ACL_*` grants in that account across
 * nine accounts, so it means access-vs-none rather than write-vs-read. Both readings mattered — under
 * the write-vs-read reading, rendering it as granted/not-granted would tell a user with read-only
 * access that they have none.
 *
 * **This tab does not decide whether it is shown.** FR-006's omission is the tab strip's job, driven
 * by `hasAccountHierarchy` in `MyInfo`, because the tab has to appear *before* this query resolves —
 * it is the slowest endpoint (~5–6s) and blocking the strip on it would leave the page empty for
 * seconds. So a tab that renders at all renders content, loading, or failure — never "not applicable".
 */
export const AccountAccessTab = ({ subject }: AccountAccessTabProps) => {
  const { data, isPending, isError } = useGetAccountAccess({
    userId: subject.userId,
    accountId: subject.accountId
  });

  const roots = data?.accountPermissions ?? [];

  return (
    <TabState
      isEmpty={roots.length === 0}
      isError={isError}
      isLoading={isPending}
      loadingActionName={"Loading My Info account access"}
      testId={"account-access"}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", rowGap: SPACE_BLOCK }}
      >
        <ReadOnlyNotice />

        {/*
         * The approved prototype's intro copy. It answers the question the bare tree provokes — a
         * list of checked and unchecked accounts, none of them interactive, invites "what am I
         * looking at, and why can't I change it?" — and it names the relationship to Feature Access,
         * which is otherwise only discoverable by opening that tab and inferring it.
         */}
        <Typography
          color={"sem.color.on-surface.subdued"}
          data-testid={"account-access-intro"}
          tag={"p"}
          variant={"body-sm"}
        >
          {Locale.getText("ACCT_INTRO") as string}
        </Typography>

        {/*
         * A single list holds the roots; `AccountNode` supplies each `<li>` and recurses. Real nested
         * lists rather than flattened rows with indentation, so the hierarchy is conveyed
         * programmatically (FR-050).
         */}
        <Box
          component={"ul"}
          data-testid={"account-access-tree"}
          sx={{ margin: 0, paddingLeft: 0 }}
        >
          {roots.map(root => (
            <AccountNode key={root.accountId} node={root} />
          ))}
        </Box>
      </Box>
    </TabState>
  );
};
